import loadFont from '@/utils/load-font.js';
import LRUCache from '@/utils/lru-cache.js';


// Make SURE this code matches ellacard-video-service:app/ev_render_video.py


// How much padding to surround the text with. Note that we apply this in all cases, not just when the media has a
// background color, in order to give us a buffer for characters that might overlap the bounds, like the end of a line
// with an italicized character.
const OUTLINE_EM = .5;


function resizeTextEl(el, media, outline=OUTLINE_EM, ignoreAddtlCSS=false) {
  el.classList.add('group-video-text');
  el.classList[ media.bold ? 'add' : 'remove' ]('pseudo-bold');
  el.style.display = 'table-cell';
  el.style.textAlign = media.justify || 'center';
  el.style.verticalAlign = !media.align || media.align == 'center' ? 'middle' : media.align;
  el.style.fontFamily = media.font;
  el.style.fontSize = media.font_size + 'px';
  el.style.color = media.color;

  if (!ignoreAddtlCSS) {
    el.style.fontStyle = media.italic ? 'italic' : null;
    el.style.textDecoration = media.underline ? 'underline' : null;
    el.style.textShadow = media.shadow || null;
  }

  // Note that 'scrollWidth' and 'scrollHeight' always return integers
  const w = Math.ceil(media.size.w);
  const h = Math.ceil(media.size.h);

  // Note that we actually subtract 1 from the scrollHeight and scrollWidth - that's beause on iOS, it was actually
  // reading one pixel too wide, presumably because of some rounding differences. You could reproduce this by scaling a
  // text template blob up to full screen (using the fit-to-screen button). On Chrome, it would resize correctly, but
  // not on iOS.

  let fontSize = media.font_size;
  el.style.fontSize = fontSize + 'px';
  el.style.width = el.style.maxWidth = (w - fontSize * outline * 2) + 'px';
  while (el.scrollHeight - 1 > h - fontSize * outline * 2) {
    fontSize -= 1;
    el.style.fontSize = fontSize + 'px';
    el.style.width = el.style.maxWidth = (w - fontSize * outline * 2) + 'px';
    if (fontSize < media.min_font_size) {
      el.style.fontSize = media.min_font_size + 'px';
      el.style.width = el.style.maxWidth = (w - media.min_font_size * outline * 2) + 'px';
      return media.min_font_size;
    }
  }
  while (el.scrollWidth - 1 > w - fontSize * outline * 2) {
    fontSize -= 1;
    el.style.fontSize = fontSize + 'px';
    el.style.width = el.style.maxWidth = (w - fontSize * outline * 2) + 'px';
    if (fontSize < media.min_font_size) {
      el.style.fontSize = media.min_font_size + 'px';
      el.style.width = el.style.maxWidth = (w - media.min_font_size * outline * 2) + 'px';
      return media.min_font_size;
    }
  }
  return fontSize;
}


async function nodeToPNG(node, w, h) {
  // Warning: this mutates the node
  node.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
  const nodeStr = new XMLSerializer().serializeToString(node).replace(/#/g, '%23').replace(/\n/g, '%0A');
  const svg = `data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><foreignObject x="0" y="0" width="100%" height="100%">${nodeStr}</foreignObject></svg>`;
  const img = new Image();
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = svg;
  });
  await new Promise(resolve => setTimeout(resolve, 100));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d').drawImage(img, 0, 0);
  return canvas.toDataURL();
}


const URL_REGEX = /url\(['"]?([^'"]+?)['"]?\)/g;


async function buildTextImgSrcKernel(media) {
  // We need to load the remote web font AND in-line it so it can be used in an SVG.
  const url = await loadFont(media.font);
  const style = document.createElement('style');
  style.appendChild(document.createTextNode(`@font-face { font-family: ${media.font}; src: url(${url}); }`));

  const container = document.createElement('div');
  container.style.width = media.size.w + 'px';

  const div = document.createElement('div');
  const span = document.createElement('span');
  span.innerText = media.text;
  span.style.background = media.background || 'transparent';
  span.style.outline = `${OUTLINE_EM}em solid ${media.background || 'transparent'}`;
  container.appendChild(style);
  container.appendChild(div);
  div.appendChild(span);

  // Note: it's important for text resizing that the container is actually part of the DOM
  document.body.appendChild(container);

  const fontSize = resizeTextEl(div, media);
  div.style.height = (media.size.h - fontSize * OUTLINE_EM * 2) + 'px';
  container.style.height = media.size.h + 'px';
  container.style.padding = Math.floor(fontSize * OUTLINE_EM) + 'px';

  // Note: I experimented with using dom-to-image here, but it's ultimately overkill and has a few issues, namely it
  // makes remote calls to load EVERY font in the document, when we know we only need the ONE in question. We also
  // don't need to support images, and I'm always in favor of removing dependencies...
  const png = await nodeToPNG(container, media.size.w, media.size.h);
  container.remove();
  return png;
}

const cache = new LRUCache(64);

async function buildTextImgSrc(media) {
  // Given a TEXT media element, this function will render the text into a PNG image using the appropriate font / style
  // and return the result as a data URL. It is cached.
  const key = JSON.stringify(media);
  let src = cache.get(key);
  if (src)
    return src;
  src = await buildTextImgSrcKernel(media);
  cache.set(key, src);
  return src;
}


export { OUTLINE_EM, resizeTextEl, buildTextImgSrc };
